/**
 * Enhanced tree builder that supports configurable maximum header depth.
 * When paths exceed the max depth, middle segments are collapsed and can be shown via tooltip.
 */

import {
  GridColumnGroup,
  GridColumnNode,
  GridLeafColumn,
} from '@mui/x-data-grid-pro';
import {partition} from 'lodash';

export interface CollapsedPathInfo {
  fullPath: string;
  collapsedPath: string;
  hiddenSegments: string[];
}

export interface TreeBuildOptions {
  maxDepth?: number; // Default 2 for max 2 header rows
  pathMapping?: Map<string, CollapsedPathInfo>;
}

export function buildTreeWithDepth(
  paths: string[],
  options: TreeBuildOptions = {}
): GridColumnGroup {
  const {maxDepth = 2, pathMapping = new Map()} = options;
  const root: GridColumnGroup = {groupId: '', children: []};

  // Process each path
  for (const str of paths) {
    const fields = str.split('.');
    const {collapsedFields, hiddenSegments} = analyzePathCollapse(
      fields,
      maxDepth
    );

    // Store mapping info if path was collapsed
    if (hiddenSegments.length > 0) {
      pathMapping.set(str, {
        fullPath: str,
        collapsedPath: collapsedFields.join('.'),
        hiddenSegments,
      });
    }

    addToTreeWithDepth(root, collapsedFields, str, 0, maxDepth, []);
  }

  return pushLeavesIntoGroupsForGroup(root);
}

function analyzePathCollapse(
  fields: string[],
  maxDepth: number
): {collapsedFields: string[]; hiddenSegments: string[]} {
  if (fields.length <= maxDepth) {
    return {
      collapsedFields: fields,
      hiddenSegments: [],
    };
  }

  let collapsedFields: string[];
  let hiddenSegments: string[];

  // If maxDepth is 1, we just have the last field
  if (maxDepth === 1) {
    collapsedFields = [fields[fields.length - 1]];
    hiddenSegments = fields.slice(0, -1);
  }
  // If maxDepth is 2, we have [first, last]
  else if (maxDepth === 2) {
    collapsedFields = [fields[0], fields[fields.length - 1]];
    hiddenSegments = fields.slice(1, -1);
  }
  // For maxDepth >= 3, we want to show:
  // - The first field
  // - The last (maxDepth - 1) fields from the end
  else {
    const startIndex = Math.max(1, fields.length - (maxDepth - 1));
    collapsedFields = [fields[0], ...fields.slice(startIndex)];

    // Hidden segments are from index 1 to startIndex (exclusive)
    hiddenSegments = fields.slice(1, startIndex);
  }

  return {collapsedFields, hiddenSegments};
}

function addToTreeWithDepth(
  node: GridColumnGroup,
  fields: string[],
  fullPath: string,
  depth: number,
  maxDepth: number,
  collapsedPath: string[] = []
): void {
  if (!fields.length) {
    return;
  }

  if (fields.length === 1) {
    // Leaf node
    node.children.push({
      field: fullPath,
    });
    return;
  }

  // Update collapsed path as we traverse
  const newCollapsedPath = [...collapsedPath, fields[0]];

  // Look for existing group
  for (const child of node.children) {
    if ('groupId' in child && child.headerName === fields[0]) {
      addToTreeWithDepth(
        child as GridColumnGroup,
        fields.slice(1),
        fullPath,
        depth + 1,
        maxDepth,
        newCollapsedPath
      );
      return;
    }
  }

  // Create new group
  // The groupId should be based on the collapsed path, not the full path
  const newNode: GridColumnGroup = {
    headerName: fields[0],
    groupId: newCollapsedPath.join('.'),
    children: [],
  };
  node.children.push(newNode);
  addToTreeWithDepth(
    newNode,
    fields.slice(1),
    fullPath,
    depth + 1,
    maxDepth,
    newCollapsedPath
  );
}

function nodeIsGroup(node: GridColumnNode): node is GridColumnGroup {
  return 'groupId' in node;
}

function pushLeavesIntoGroupsForGroup(group: GridColumnGroup): GridColumnGroup {
  const originalChildren = group.children;

  const [leaves, groups] = partition(
    group.children,
    (child): child is GridLeafColumn => !nodeIsGroup(child)
  );

  const childrenLeaves: {[key: string]: GridLeafColumn} = Object.fromEntries(
    leaves.map(child => [child.field, child])
  );

  const childrenGroups: {[key: string]: GridColumnGroup} = Object.fromEntries(
    (groups as GridColumnGroup[]).map(child => [child.groupId, child])
  );

  // First, push leaves into groups
  // Optimization: Use map-based lookup instead of O(n*m) nested iteration
  if (
    Object.keys(childrenGroups).length > 0 &&
    Object.keys(childrenLeaves).length > 0
  ) {
    // Determine group depth from any group key (all groups at same level have same depth)
    const groupDepth = Object.keys(childrenGroups)[0].split('.').length;

    // Create prefix-to-group map for O(1) lookup
    const prefixToGroup = new Map<string, GridColumnGroup>();
    Object.entries(childrenGroups).forEach(([groupKey, group]) => {
      prefixToGroup.set(groupKey, group);
    });

    // Process each leaf with direct prefix lookup
    Object.entries(childrenLeaves).forEach(([childKey, leaf]) => {
      const segments = childKey.split('.');
      if (segments.length >= groupDepth) {
        const prefix = segments.slice(0, groupDepth).join('.');
        const group = prefixToGroup.get(prefix);
        if (group) {
          group.children.push(leaf);
          delete childrenLeaves[childKey];
        }
      }
    });
  }

  // Next, recursively apply the same logic to the groups
  Object.keys(childrenGroups).forEach(key => {
    childrenGroups[key] = pushLeavesIntoGroupsForGroup(childrenGroups[key]);
  });

  const finalChildren = originalChildren
    .map(child => {
      if (nodeIsGroup(child)) {
        return childrenGroups[child.groupId];
      } else {
        return childrenLeaves[child.field];
      }
    })
    .filter((child): child is GridColumnNode => !!child);

  const newGroup: GridColumnGroup = {
    ...group,
    children: finalChildren,
  };

  return newGroup;
}

/**
 * Escapes literal periods in field names from a flattened string and segment list.
 *
 * @example
 * // Field with literal period in key
 * escapeFieldForQuery("output.double.nested", ["output", "double.nested"])
 * // Returns: "output.double\\.nested"
 */
export function escapeFieldForQuery(
  field: string,
  segments?: string[]
): string {
  if (!segments) {
    return field;
  }
  return segments
    .map(segment => {
      if (segment.includes('.')) {
        // Escape all periods in this segment
        return segment.replace(/\./g, '\\.');
      }
      return segment;
    })
    .join('.');
}
